<script setup>
import { defineAsyncComponent, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import HeroSection from './components/HeroSection.vue'
import ImplementationProof from './components/ImplementationProof.vue'
import LazySection from './components/ui/LazySection.vue'
import SiteHeader from './components/SiteHeader.vue'
import { useTopSentinel } from './composables/useScrolledPast'

/**
 * Only the header, hero, and the block directly under it are in the initial
 * bundle. Everything below the fold is a separate chunk fetched by
 * `LazySection` as the reader approaches it, so the first paint parses a
 * fraction of the JavaScript it used to.
 */
const EcosystemSection = defineAsyncComponent(() => import('./components/EcosystemSection.vue'))
const DifferentiatorsSection = defineAsyncComponent(() =>
  import('./components/DifferentiatorsSection.vue')
)
const PlatformSection = defineAsyncComponent(() => import('./components/PlatformSection.vue'))
const PresensiSection = defineAsyncComponent(() => import('./components/PresensiSection.vue'))
const IndustriesSection = defineAsyncComponent(() => import('./components/IndustriesSection.vue'))
const ProblemsScene = defineAsyncComponent(() => import('./components/ProblemsScene.vue'))
const IntelligenceSection = defineAsyncComponent(() => import('./components/IntelligenceSection.vue'))
const TechnologySection = defineAsyncComponent(() => import('./components/TechnologySection.vue'))
const WhyCustomSection = defineAsyncComponent(() => import('./components/WhyCustomSection.vue'))
const TestimonialsSection = defineAsyncComponent(() => import('./components/TestimonialsSection.vue'))
const ContactSection = defineAsyncComponent(() => import('./components/ContactSection.vue'))
const ClosingCta = defineAsyncComponent(() => import('./components/ClosingCta.vue'))
const SiteFooter = defineAsyncComponent(() => import('./components/SiteFooter.vue'))
const FloatingWhatsApp = defineAsyncComponent(() => import('./components/FloatingWhatsApp.vue'))
const DemoModal = defineAsyncComponent(() => import('./components/DemoModal.vue'))

const { t } = useI18n()
const { sentinel } = useTopSentinel()

onMounted(() => {
  // The dialog opens on click, so its chunk must already be there — but it has
  // no business competing with the first paint. Warm it during idle time.
  const warm = () => import('./components/DemoModal.vue')
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 })
  else setTimeout(warm, 2500)
})
</script>

<template>
  <div class="w-full overflow-x-hidden">
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-ink focus:px-4 focus:py-2 focus:text-white"
    >
      {{ t('ui.nav.skip') }}
    </a>

    <!-- Watched by useTopSentinel so the header needs no scroll listener. -->
    <div ref="sentinel" aria-hidden="true" class="pointer-events-none absolute top-0 h-6 w-px" />

    <SiteHeader />

    <main id="main">
      <HeroSection />
      <ImplementationProof />

      <LazySection min-height="900px"><EcosystemSection /></LazySection>
      <LazySection min-height="760px"><DifferentiatorsSection /></LazySection>
      <LazySection min-height="2200px"><PlatformSection /></LazySection>
      <LazySection min-height="1600px"><PresensiSection /></LazySection>
      <LazySection min-height="1200px"><IndustriesSection /></LazySection>
      <LazySection min-height="520px"><ProblemsScene /></LazySection>
      <LazySection min-height="620px"><IntelligenceSection /></LazySection>
      <LazySection min-height="760px"><TechnologySection /></LazySection>
      <LazySection min-height="1600px"><WhyCustomSection /></LazySection>
      <LazySection min-height="520px"><TestimonialsSection /></LazySection>
      <LazySection min-height="620px"><ContactSection /></LazySection>
      <LazySection min-height="1200px"><ClosingCta /></LazySection>
    </main>

    <LazySection min-height="360px"><SiteFooter /></LazySection>

    <FloatingWhatsApp />
    <DemoModal />
  </div>
</template>
